import { useEffect, useState } from 'react';
import { BellRing, LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useAlertSettings, useUpdateAlertSettings } from '@/api/hooks/useSettings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { formatDateTime } from '@/lib/utils';

/** Only rendered when the caller holds alerts.manage — there's no separate view-only permission for this. */
export function AlertSettingsCard() {
  const { t } = useTranslation();
  const { data, isLoading } = useAlertSettings();
  const updateSettings = useUpdateAlertSettings();

  const [outOfStock, setOutOfStock] = useState(true);
  const [lowStock, setLowStock] = useState(true);
  const [expiry, setExpiry] = useState(true);
  const [expiryDays, setExpiryDays] = useState('30');
  const [deadStockDays, setDeadStockDays] = useState('180');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setOutOfStock(data.alertOnOutOfStock);
      setLowStock(data.alertOnLowStock);
      setExpiry(data.alertOnExpiry);
      setExpiryDays(String(data.expiryAlertDays));
      setDeadStockDays(String(data.deadStockThresholdDays));
    }
  }, [data]);

  async function handleSave() {
    setError(null);
    try {
      await updateSettings.mutateAsync({
        alertOnOutOfStock: outOfStock,
        alertOnLowStock: lowStock,
        alertOnExpiry: expiry,
        expiryAlertDays: Number(expiryDays) || 30,
        deadStockThresholdDays: Number(deadStockDays) || 180,
      });
      toast({ title: 'Alert settings saved', variant: 'success' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  if (isLoading || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BellRing className="h-4 w-4" /> {t('alertsTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">{t('outOfStock')}</span>
            <Switch checked={outOfStock} onCheckedChange={setOutOfStock} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">{t('lowStock')}</span>
            <Switch checked={lowStock} onCheckedChange={setLowStock} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">{t('expiringSoon')}</span>
            <Switch checked={expiry} onCheckedChange={setExpiry} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="al-days">{t('settingsExpiryDays')}</Label>
            <Input
              id="al-days"
              type="number"
              min={1}
              max={365}
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="al-dead">Dead stock threshold (days)</Label>
            <Input
              id="al-dead"
              type="number"
              min={1}
              max={3650}
              value={deadStockDays}
              onChange={(e) => setDeadStockDays(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              No sale in this many days flags an item as dead stock in Reports.
            </p>
          </div>
        </div>

        {data.lastAlertSent && (
          <p className="text-xs text-muted-foreground">Last alert sent {formatDateTime(data.lastAlertSent)}</p>
        )}

        {error && (
          <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
            {error}
          </p>
        )}

        <Button onClick={handleSave} disabled={updateSettings.isPending}>
          {updateSettings.isPending && <LoaderCircle className="animate-spin" />}
          Save alert settings
        </Button>
      </CardContent>
    </Card>
  );
}
